import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _PropTypes from 'prop-types';
import { rgba } from 'polished';
import * as React from 'react';
import { createElement } from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';
import styled from 'styled-components';
import { Button } from '../../../Button/Button.js';
import { Checkbox } from '../../../Checkbox/Checkbox.js';
import { IconArrowDown } from '../../../Icons/IconArrowDown.js';
import { IconArrowRight } from '../../../Icons/IconArrowRight.js';
import { IconDrag } from '../../../Icons/IconDrag.js';
import { Tooltip } from '../../../Tooltip/Tooltip.js';
import { TREE_LINE_HEIGHT, TREE_HORIZONTAL_SEPARATION, TREE_DEFAULT_BORDER_WIDTH, TREE_DRAG_TYPE } from '../../constants.js';
import { dnd } from '../../Tree.js';
import { isTreeDropNodeValid } from '../../utils.js';
import { TreeDragHandle } from '../TreeDragHandle.js';
import { TreeLabel } from '../TreeLabel.js';
import { Descender } from './Descender.js';
import { TreeDropBar } from './TreeDropBar.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

var StyledContainer = styled.div.withConfig({
  displayName: "TreeNode__StyledContainer",
  componentId: "sc-1ruxqz3-0"
})(["&&{display:flex;}"]);
var StyledPrefixIcons = styled.div.withConfig({
  displayName: "TreeNode__StyledPrefixIcons",
  componentId: "sc-1ruxqz3-1"
})(["&&{flex:0;position:relative;}"]);
var StyledHoverCapture = styled.div.withConfig({
  displayName: "TreeNode__StyledHoverCapture",
  componentId: "sc-1ruxqz3-2"
})(["&&{flex:1;}"]);
var StyledExpandIconWrapper = styled.div.withConfig({
  displayName: "TreeNode__StyledExpandIconWrapper",
  componentId: "sc-1ruxqz3-3"
})(["&&{inset-inline-start:", "px;inset-block-start:", "px;position:absolute;}"], function (props) {
  return props.descenderCount * props.width - 2;
}, function (props) {
  return props.height / 2 - 11;
});
var StyledIconWrapper = styled.div.withConfig({
  displayName: "TreeNode__StyledIconWrapper",
  componentId: "sc-1ruxqz3-4"
})(function () {
  return {
    lineHeight: TREE_LINE_HEIGHT,
    marginInlineStart: TREE_HORIZONTAL_SEPARATION
  };
});
var StyledTreeNode = styled.div.withConfig({
  displayName: "TreeNode__StyledTreeNode",
  componentId: "sc-1ruxqz3-5"
})(function (_ref) {
  var emphasized = _ref.emphasized,
      isDragging = _ref.isDragging,
      isDragOver = _ref.isDragOver,
      selected = _ref.selected,
      theme = _ref.theme;
  return _objectSpread(_objectSpread({}, function () {
    var defaultPadding = "2px";

    if (isDragging) {
      return {
        backgroundColor: theme.neutralColors.pearl,
        padding: defaultPadding
      };
    } else if (isDragOver) {
      return {
        backgroundColor: rgba(theme.CTAColors.primaryVariant, 0.1),
        border: "dashed ".concat(TREE_DEFAULT_BORDER_WIDTH, "px ").concat(theme.CTAColors.primaryVariant),
        padding: 0
      };
    } else if (selected) {
      return {
        backgroundColor: theme.neutralColors.silver,
        borderInlineStart: "solid 4px ".concat(theme.neutralColors.charcoal),
        padding: defaultPadding
      };
    } else if (emphasized) {
      return {
        backgroundColor: theme.neutralColors.pearl,
        padding: defaultPadding
      };
    } else {
      return {
        padding: defaultPadding
      };
    }
  }()), {}, {
    borderRadius: 2,
    display: "flex",
    position: "relative"
  });
});

var TreeNode = function TreeNode(_ref2) {
  var a11yDragId = _ref2.a11yDragId,
      canOrder = _ref2.canOrder,
      _ref2$canDrag = _ref2.canDrag,
      _canDrag = _ref2$canDrag === void 0 ? function () {
    return true;
  } : _ref2$canDrag,
      _ref2$canDrop = _ref2.canDrop,
      _canDrop = _ref2$canDrop === void 0 ? function () {
    return true;
  } : _ref2$canDrop,
      context = _ref2.context,
      focused = _ref2.focused,
      initialHeight = _ref2.height,
      node = _ref2.node,
      onChange = _ref2.onChange,
      onCollapse = _ref2.onCollapse,
      onDragStart = _ref2.onDragStart,
      onExpand = _ref2.onExpand,
      renderButtons = _ref2.renderButtons,
      renderIcon = _ref2.renderIcon,
      renderLabel = _ref2.renderLabel,
      variant = _ref2.variant;

  var ref = React.useRef(null);

  var _React$useState = React.useState(initialHeight || 0),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      height = _React$useState2[0],
      setHeight = _React$useState2[1];

  var _React$useState3 = React.useState(false),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      hovered = _React$useState4[0],
      setHovered = _React$useState4[1];

  React.useEffect(function () {
    var _ref$current;

    setHeight(((_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.clientHeight) || initialHeight || 0);
  }, [initialHeight]);
  var width = 32;
  var emphasized = focused || hovered;
  var selected = React.useMemo(function () {
    if ((variant === null || variant === void 0 ? void 0 : variant.type) === "select") {
      return variant.isSelected({
        context: context,
        node: node
      });
    } else {
      return false;
    }
  }, [context, node, variant]);

  var _dnd$useDrag = dnd.useDrag(function () {
    return {
      canDrag: function canDrag() {
        return !!onChange && !a11yDragId && _canDrag({
          context: context,
          node: node
        });
      },
      collect: function collect(monitor) {
        return {
          canDragNode: monitor.canDrag(),
          isDragging: monitor.isDragging()
        };
      },
      item: function item() {
        if (onDragStart) {
          onDragStart({
            level: context.getAriaLevel()
          });
        }

        return {
          context: context,
          node: node
        };
      },
      type: TREE_DRAG_TYPE
    };
  }, [context]),
      _dnd$useDrag2 = _slicedToArray(_dnd$useDrag, 3),
      _dnd$useDrag2$ = _dnd$useDrag2[0],
      canDragNode = _dnd$useDrag2$.canDragNode,
      isDragging = _dnd$useDrag2$.isDragging,
      connectDragSource = _dnd$useDrag2[1],
      connectDragPreview = _dnd$useDrag2[2];

  var _dnd$useDrop = dnd.useDrop(function () {
    return {
      accept: TREE_DRAG_TYPE,
      canDrop: function canDrop(source) {
        return isTreeDropNodeValid({
          canDrop: _canDrop,
          source: source,
          target: {
            context: context,
            node: node
          }
        });
      },
      drop: function drop(source, monitor) {
        if (monitor.didDrop()) {
          return;
        }

        onChange({
          position: "childof",
          source: {
            context: source.context,
            node: source.node
          },
          target: {
            context: context,
            node: node
          }
        });
      },
      collect: function collect(monitor) {
        return {
          canDropNode: monitor.canDrop(),
          isDragOverNode: monitor.isOver()
        };
      }
    };
  }, [context]),
      _dnd$useDrop2 = _slicedToArray(_dnd$useDrop, 2),
      _dnd$useDrop2$ = _dnd$useDrop2[0],
      canDropNode = _dnd$useDrop2$.canDropNode,
      isDragOverNode = _dnd$useDrop2$.isDragOverNode,
      connectDropTarget = _dnd$useDrop2[1];

  var _dnd$useDrop3 = dnd.useDrop(function () {
    return {
      accept: TREE_DRAG_TYPE,
      canDrop: function canDrop(source) {
        return !!canOrder && source.context.id !== context.id && source.context.getNextSiblingId() !== context.id && !context.ancestorIds.includes(source.context.id) && _canDrop(context.parentId ? {
          context: context.getParentContext(),
          node: context.getParentNode()
        } : void 0);
      },
      drop: function drop(source) {
        onChange({
          position: "before",
          source: {
            context: source.context,
            node: source.node
          },
          target: {
            context: context,
            node: node
          }
        });
      },
      collect: function collect(monitor) {
        return {
          canDropTopBar: monitor.canDrop(),
          isDragOverTopBar: monitor.isOver()
        };
      }
    };
  }, [context]),
      _dnd$useDrop4 = _slicedToArray(_dnd$useDrop3, 2),
      _dnd$useDrop4$ = _dnd$useDrop4[0],
      canDropTopBar = _dnd$useDrop4$.canDropTopBar,
      isDragOverTopBar = _dnd$useDrop4$.isDragOverTopBar,
      connectDropTopBarTarget = _dnd$useDrop4[1];

  var _dnd$useDrop5 = dnd.useDrop(function () {
    return {
      accept: TREE_DRAG_TYPE,
      canDrop: function canDrop(source) {
        return !!canOrder && source.context.id !== context.id && source.context.getPrevSiblingId() !== context.id && !context.ancestorIds.includes(source.context.id) && _canDrop(context.parentId ? {
          context: context.getParentContext(),
          node: context.getParentNode()
        } : void 0);
      },
      drop: function drop(source) {
        onChange({
          position: "after",
          source: {
            context: source.context,
            node: source.node
          },
          target: {
            context: context,
            node: node
          }
        });
      },
      collect: function collect(monitor) {
        return {
          canDropBottomBar: monitor.canDrop(),
          isDragOverBottomBar: monitor.isOver()
        };
      }
    };
  }, [context]),
      _dnd$useDrop6 = _slicedToArray(_dnd$useDrop5, 2),
      _dnd$useDrop6$ = _dnd$useDrop6[0],
      canDropBottomBar = _dnd$useDrop6$.canDropBottomBar,
      isDragOverBottomBar = _dnd$useDrop6$.isDragOverBottomBar,
      connectDropBottomBarTarget = _dnd$useDrop6[1];

  React.useEffect(function () {
    connectDragPreview(getEmptyImage(), {
      captureDraggingState: true
    });
  }, [connectDragPreview]);
  React.useEffect(function () {
    if (isDragOverNode && context.isCollapsed()) {
      onExpand();
    }
  }, [context, isDragOverNode, onExpand]);
  var isAnyDragging = isDragging || a11yDragId === context.id;
  var isA11yDragOverNode = !!a11yDragId && !!focused && isTreeDropNodeValid({
    canDrop: _canDrop,
    source: {
      context: context.treeContext.getNodeContext(a11yDragId),
      node: context.treeContext.nodeById[a11yDragId]
    },
    target: {
      context: context,
      node: node
    }
  });
  return /*#__PURE__*/jsxs(StyledContainer, {
    ref: ref,
    children: [/*#__PURE__*/jsxs(StyledPrefixIcons, {
      children: [/*#__PURE__*/jsx(Descender, {
        hasChildren: context.hasChildren(),
        hasDescenderByLevel: context.hasDescenderByLevel,
        height: height,
        isLastChild: context.isLastChild(),
        stroke: "silver",
        width: width
      }), context.hasChildren() && height > 0 && /*#__PURE__*/jsx(StyledExpandIconWrapper, {
        descenderCount: context.hasDescenderByLevel.length,
        width: width,
        height: height,
        children: context.isCollapsed() ? /*#__PURE__*/jsx(IconArrowRight, {
          onClick: onExpand,
          size: 16
        }) : /*#__PURE__*/jsx(IconArrowDown, {
          onClick: onCollapse,
          size: 16
        })
      })]
    }), /*#__PURE__*/jsx(StyledHoverCapture, {
      onMouseEnter: function onMouseEnter() {
        return setHovered(true);
      },
      onMouseLeave: function onMouseLeave() {
        return setHovered(false);
      },
      children: /*#__PURE__*/jsxs(StyledTreeNode, _objectSpread(_objectSpread({
        emphasized: emphasized,
        isDragging: isAnyDragging,
        isDragOver: isDragOverNode && canDropNode && !(isDragOverBottomBar || isDragOverTopBar) || isA11yDragOverNode,
        ref: connectDropTarget
      }, (variant === null || variant === void 0 ? void 0 : variant.type) === "select" ? {
        selected: selected,
        onClick: function onClick() {
          return variant.onSelect({
            context: context,
            node: node
          });
        }
      } : void 0), {}, {
        children: [function () {
          if (isDragOverNode) {
            return /*#__PURE__*/jsxs(Fragment, {
              children: [/*#__PURE__*/jsx(TreeDropBar, {
                position: "bottom",
                isDragOver: isDragOverBottomBar && canDropBottomBar,
                ref: connectDropBottomBarTarget
              }), /*#__PURE__*/jsx(TreeDropBar, {
                position: "top",
                isDragOver: isDragOverTopBar && canDropTopBar,
                ref: connectDropTopBarTarget
              })]
            });
          }
        }(), onChange && hovered && /*#__PURE__*/jsx(TreeDragHandle, {
          disabled: !canDragNode,
          isDragPlaceholder: isAnyDragging,
          ref: connectDragSource,
          children: /*#__PURE__*/jsx(IconDrag, {
            size: 16
          })
        }), function () {
          if (!isAnyDragging && (variant === null || variant === void 0 ? void 0 : variant.type) === "multiselect") {
            var checkboxProps = variant.renderCheckbox({
              context: context,
              node: node
            });
            return /*#__PURE__*/jsx(StyledIconWrapper, {
              children: /*#__PURE__*/jsx(Checkbox, _objectSpread(_objectSpread({}, checkboxProps), {}, {
                "aria-label": "Select item",
                onFocus: function onFocus(event) {
                  return event.stopPropagation();
                },
                size: 16,
                tabIndex: focused && !checkboxProps.disabled ? 0 : -1
              }))
            });
          }
        }(), function () {
          if (!isAnyDragging && renderIcon) {
            var el = renderIcon({
              context: context,
              node: node
            });

            if (el) {
              return /*#__PURE__*/jsx(StyledIconWrapper, {
                children: /*#__PURE__*/React.cloneElement(el, {
                  size: 16
                })
              });
            }
          }
        }(), /*#__PURE__*/jsx(TreeLabel, {
          isDragging: isAnyDragging,
          children: renderLabel({
            context: context,
            node: node
          })
        }), !isAnyDragging && renderButtons && (hovered || selected) && renderButtons({
          context: context,
          node: node
        }).map(function (_ref3, i) {
          var buttonProps = _ref3.buttonProps,
              tooltipProps = _ref3.tooltipProps;

          var buttonEl = /*#__PURE__*/createElement(Button, _objectSpread(_objectSpread({}, buttonProps), {}, {
            hideLabel: true,
            key: i,
            label: "",
            onFocus: function onFocus(event) {
              return event.stopPropagation();
            },
            onKeyDown: function onKeyDown(event) {
              return event.stopPropagation();
            },
            variant: "ghost"
          }));

          if (!tooltipProps) {
            return buttonEl;
          } else {
            return /*#__PURE__*/createElement(Tooltip, _objectSpread(_objectSpread({}, tooltipProps), {}, {
              key: i
            }), buttonEl);
          }
        })]
      }))
    })]
  });
};

TreeNode.propTypes = {
  "a11yDragId": _PropTypes.string,
  "canDrag": _PropTypes.func,
  "canDrop": _PropTypes.func,
  "canOrder": _PropTypes.bool,
  "context": _PropTypes.shape({
    "absoluteIndex": _PropTypes.number.isRequired,
    "ancestorIds": _PropTypes.arrayOf(_PropTypes.string).isRequired,
    "getAriaExpanded": _PropTypes.func.isRequired,
    "getAriaLevel": _PropTypes.func.isRequired,
    "getAriaPosInSet": _PropTypes.func.isRequired,
    "getAriaRowIndex": _PropTypes.func.isRequired,
    "getAriaSetSize": _PropTypes.func.isRequired,
    "getChildIds": _PropTypes.func.isRequired,
    "getNextSiblingId": _PropTypes.func.isRequired,
    "getParentContext": _PropTypes.func.isRequired,
    "getParentNode": _PropTypes.func.isRequired,
    "getPrevSiblingId": _PropTypes.func.isRequired,
    "getSiblingsAndSelf": _PropTypes.func.isRequired,
    "hasChildren": _PropTypes.func.isRequired,
    "hasDescenderByLevel": _PropTypes.arrayOf(_PropTypes.bool).isRequired,
    "id": _PropTypes.string.isRequired,
    "isCollapsed": _PropTypes.func.isRequired,
    "isExpanded": _PropTypes.func.isRequired,
    "isLastChild": _PropTypes.func.isRequired,
    "parentId": _PropTypes.string,
    "traverseDepthFirst": _PropTypes.func.isRequired,
    "treeContext": _PropTypes.shape({
      "childIdsByParentId": _PropTypes.object.isRequired,
      "getLastVisibleIndex": _PropTypes.func.isRequired,
      "getNextVisibleAbsoluteIndex": _PropTypes.func.isRequired,
      "getNextVisibleIndexByIndex": _PropTypes.func.isRequired,
      "getNodeContext": _PropTypes.func.isRequired,
      "getNodeCount": _PropTypes.func.isRequired,
      "getPrevVisibleAbsoluteIndex": _PropTypes.func.isRequired,
      "getPrevVisibleIndexByIndex": _PropTypes.func.isRequired,
      "hiddenIds": _PropTypes.object.isRequired,
      "lastVisibleIndex": _PropTypes.number,
      "nextVisibleIndexByIndex": _PropTypes.object,
      "nodeById": _PropTypes.object.isRequired,
      "nodeContextById": _PropTypes.object.isRequired,
      "nodeCount": _PropTypes.number,
      "nodeIds": _PropTypes.arrayOf(_PropTypes.string).isRequired,
      "nodes": _PropTypes.arrayOf(_PropTypes.shape({
        "parentId": _PropTypes.string
      })).isRequired,
      "prevVisibleIndexByIndex": _PropTypes.object,
      "rootIds": _PropTypes.arrayOf(_PropTypes.string).isRequired,
      "traverseDepthFirst": _PropTypes.func.isRequired
    }).isRequired
  }).isRequired,
  "focused": _PropTypes.bool,
  "height": _PropTypes.number,
  "node": _PropTypes.shape({
    "parentId": _PropTypes.string
  }).isRequired,
  "onChange": _PropTypes.func,
  "onCollapse": _PropTypes.func.isRequired,
  "onDragStart": _PropTypes.func,
  "onExpand": _PropTypes.func.isRequired,
  "renderButtons": _PropTypes.func,
  "renderIcon": _PropTypes.func,
  "renderLabel": _PropTypes.func.isRequired,
  "variant": _PropTypes.oneOfType([_PropTypes.shape({
    isSelected: _PropTypes.func,
    onSelect: _PropTypes.func,
    type: _PropTypes.oneOf(["select"])
  }), _PropTypes.shape({
    onDefaultAction: _PropTypes.func,
    renderCheckbox: _PropTypes.func,
    type: _PropTypes.oneOf(["multiselect"])
  })])
};
TreeNode.displayName = "TreeNode";

export { StyledTreeNode, TreeNode };
